<script setup>
/* eslint-disable no-console */
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Modal from 'dashboard/components/Modal.vue';
import MessageAPI from 'dashboard/api/inbox/message';
import MeetingsAPI from 'dashboard/api/meetings';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { copyTextToClipboard } from 'shared/helpers/clipboard';

const props = defineProps({
  conversationId: {
    type: Number,
    required: true,
  },
  isPrivateNote: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const store = useStore();

// Modal state
const showModal = ref(false);
const meetingUrl = ref('');
const meetingRoomId = ref('');
const isSending = ref(false);

// Get URL_MEET from globalConfig
const meetUrl = computed(() => window.globalConfig?.URL_MEET || '');

// Get accountId from store
const accountId = computed(() => store.getters.getCurrentAccountId);

// Get current user for display name
const currentUser = computed(() => store.getters.getCurrentUser);

// Display name with fallback to full name
const userDisplayName = computed(() => {
  const user = currentUser.value;
  return user?.display_name || user?.name || '';
});

// Check if meet_video feature is enabled for the account
const isMeetVideoEnabled = computed(() => {
  if (!accountId.value) return false;
  return store.getters['accounts/isFeatureEnabledonAccount'](
    accountId.value,
    FEATURE_FLAGS.MEET_VIDEO
  );
});

// Check if button should be displayed
const showButton = computed(() => {
  return !!meetUrl.value && !props.isPrivateNote && isMeetVideoEnabled.value;
});

/**
 * Generate a short room ID in the format xxx-xxxx-xxx (similar to Google Meet)
 */
const generateRoomId = () => {
  const chars = 'abcdefghijklmnopqrstuvwxyz';
  const generatePart = length => {
    let result = '';
    for (let i = 0; i < length; i += 1) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };
  return `${generatePart(3)}-${generatePart(4)}-${generatePart(3)}`;
};

/**
 * Build the full meeting URL and store it for the modal
 */
const buildMeetingUrl = () => {
  if (!meetUrl.value || !accountId.value || !props.conversationId) {
    console.error('[JitsiMeet] Cannot build URL: missing required data', {
      meetUrl: !!meetUrl.value,
      accountId: accountId.value,
      conversationId: props.conversationId,
    });
    return false;
  }

  const roomId = generateRoomId();
  const baseUrl = `${meetUrl.value}/${roomId}/${accountId.value}/${props.conversationId}`;
  const displayName = encodeURIComponent(`com ${userDisplayName.value}`);

  meetingRoomId.value = roomId;
  meetingUrl.value = `${baseUrl}?meet=${displayName}`;
  return true;
};

/**
 * Register the room with the Jitsi API via the backend (fire-and-forget style).
 */
const registerRoom = async () => {
  try {
    // console.log(
    //   '[JitsiMeet] Registering room for expiration:',
    //   meetingRoomId.value
    // );
    const response = await MeetingsAPI.registerRoom({
      roomId: meetingRoomId.value,
      ttl: 2592000,
      info: `com ${userDisplayName.value}`,
    });

    const { success, status } = response.data || {};

    if (success) {
      // console.log(
      //   '[JitsiMeet] ✅ Room registered with Jitsi API:',
      //   meetingRoomId.value
      // );
    } else if (status === 'skipped') {
      // console.warn(
      //   '[JitsiMeet] ⚠️ Registration SKIPPED — missing server config:',
      //   reason,
      //   '(check MEET_API_KEY and URL_MEET in Super Admin)'
      // );
    } else if (status === 'retry_enqueued') {
      // console.warn(
      //   '[JitsiMeet] ⏳ First attempt failed, background retries enqueued:',
      //   meetingRoomId.value
      // );
    } else {
      // console.warn(
      //   '[JitsiMeet] ❌ Registration failed:',
      //   status,
      //   reason,
      //   response.data
      // );
    }
  } catch (error) {
    // console.warn(
    //   '[JitsiMeet] Room registration failed (fail-open):',
    //   error?.message || error
    // );
  }
};

/**
 * Copy the meeting link to clipboard
 */
const copyLink = async () => {
  try {
    await copyTextToClipboard(meetingUrl.value);
    useAlert(t('CONVERSATION.JITSI_MEET.COPY_SUCCESS'));
    // console.log(
    //   '[JitsiMeet] Meeting link copied to clipboard:',
    //   meetingUrl.value
    // );
  } catch (error) {
    // console.error('[JitsiMeet] Failed to copy link to clipboard:', error);
    useAlert(t('CONVERSATION.JITSI_MEET.ERROR'));
  }
};

/**
 * Send the meeting link as a message in the conversation
 */
const sendMeetingLink = async () => {
  isSending.value = true;
  const messageTemplate = t('CONVERSATION.JITSI_MEET.MESSAGE_TEMPLATE', {
    url: meetingUrl.value,
  });

  try {
    await MessageAPI.create({
      conversationId: props.conversationId,
      message: messageTemplate,
      private: false,
    });
    // console.log(
    //   '[JitsiMeet] Meeting link sent successfully:',
    //   meetingUrl.value
    // );
    useAlert(t('CONVERSATION.JITSI_MEET.SUCCESS'));
    showModal.value = false;
  } catch (error) {
    // console.error('[JitsiMeet] Failed to send meeting link:', error);
    useAlert(t('CONVERSATION.JITSI_MEET.ERROR'));
  } finally {
    isSending.value = false;
  }
};

/**
 * Close the modal
 */
const closeModal = () => {
  showModal.value = false;
};

/**
 * Handler for button click: generate URL, register room, then open modal
 */
const handleClick = async () => {
  const built = buildMeetingUrl();
  if (!built) {
    useAlert(t('CONVERSATION.JITSI_MEET.ERROR'));
    return;
  }

  // Register room in background (don't await — fail-open)
  // .catch prevents unhandled promise rejection in strict environments
  registerRoom().catch(() => {});

  // Open the modal with the URL ready
  showModal.value = true;
};
</script>

<template>
  <NextButton
    v-if="showButton"
    v-tooltip.top-end="$t('CONVERSATION.JITSI_MEET.TOOLTIP')"
    icon="i-ph-video-camera"
    slate
    faded
    sm
    @click="handleClick"
  />
  <Modal v-model:show="showModal" :on-close="closeModal">
    <div class="h-auto overflow-auto flex flex-col">
      <woot-modal-header
        :header-title="$t('CONVERSATION.JITSI_MEET.CONFIRM_TITLE')"
        :header-content="$t('CONVERSATION.JITSI_MEET.CONFIRM_DESCRIPTION')"
      />
      <div class="flex flex-row justify-end gap-2 py-4 px-6 w-full">
        <NextButton
          faded
          :label="$t('CONVERSATION.JITSI_MEET.COPY_BUTTON')"
          icon="i-ph-copy"
          @click="copyLink"
        />
        <NextButton
          faded
          type="reset"
          :label="$t('CONVERSATION.JITSI_MEET.CANCEL_BUTTON')"
          @click="closeModal"
        />
        <NextButton
          type="submit"
          :label="$t('CONVERSATION.JITSI_MEET.CONFIRM_BUTTON')"
          :disabled="isSending"
          @click="sendMeetingLink"
        />
      </div>
    </div>
  </Modal>
</template>
